import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ROLES } from '../mock';
import { computeScores } from '../lib/scoring';
import { fmtDate, scoreColor, daysUntil } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Building2, ClipboardList, AlertCircle, ArrowRight, TrendingUp, ShieldCheck } from 'lucide-react';
import AlertsFeed from '../components/AlertsFeed';
import AuditExportButton from '../components/AuditExportButton';
import DigestSendButton from '../components/DigestSendButton';
import DigestAnalytics from '../components/DigestAnalytics';
import RMLeaderboard from '../components/RMLeaderboard';

const StatCard = ({ icon: Icon, label, value, accent, hint }) => (
  <Card className="border-brand-border">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="text-3xl font-bold text-brand-indigo mt-2">{value}</div>
          {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboardPage = () => {
  const { user, USERS: allUsers, visibleClients, visibleSessions } = useApp();

  const salesHeads = allUsers.filter((u) => u.role === ROLES.SALES_HEAD);
  const rms = allUsers.filter((u) => u.role === ROLES.RM);

  const now = new Date();
  const thisMonth = visibleSessions.filter((s) => {
    const d = new Date(s.session_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const followupsWeek = visibleSessions.filter((s) => { const du = daysUntil(s.follow_up_date); return du !== null && du >= 0 && du <= 7; });

  const avgScore = visibleSessions.length ? Math.round(visibleSessions.reduce((a, s) => a + computeScores(s).overall, 0) / visibleSessions.length) : 0;

  const rmRows = rms.map((rm) => {
    const rmClients = visibleClients.filter((c) => c.rm_id === rm.user_id);
    const rmSessions = visibleSessions.filter((s) => s.rm_id === rm.user_id);
    const avg = rmSessions.length ? Math.round(rmSessions.reduce((a, s) => a + computeScores(s).overall, 0) / rmSessions.length) : 0;
    const sh = allUsers.find((u) => u.user_id === rm.sales_head_id);
    return { rm, sh, clients: rmClients.length, sessions: rmSessions.length, avg };
  });

  const shRows = salesHeads.map((sh) => {
    const mappedRms = rms.filter((r) => sh.rm_ids?.includes(r.user_id));
    const shClients = visibleClients.filter((c) => sh.rm_ids?.includes(c.rm_id));
    const shSessions = visibleSessions.filter((s) => sh.rm_ids?.includes(s.rm_id));
    return { sh, mappedRms, clients: shClients.length, sessions: shSessions.length };
  });

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Admin overview</div>
          <h1 className="text-3xl font-bold text-brand-indigo">{user?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.title || 'Compliance & Operations'}</p>
        </div>
        <div className="flex items-center gap-2">
          <DigestSendButton />
          <AuditExportButton />
          <Badge variant="outline" className="text-brand-indigo border-brand-border bg-white">Firm-wide view</Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Building2} label="Sales Heads" value={salesHeads.length} accent="bg-brand-indigo" />
        <StatCard icon={Users} label="Relationship Mgrs" value={rms.length} accent="bg-brand-cyan" />
        <StatCard icon={Users} label="Total Clients" value={visibleClients.length} accent="bg-brand-green" />
        <StatCard icon={ClipboardList} label="Sessions (Mo.)" value={thisMonth.length} accent="bg-brand-amber" />
        <StatCard icon={TrendingUp} label="Avg MRI Score" value={avgScore} accent="bg-brand-indigoLight" hint="across all sessions" />
      </div>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border">
            <div className="text-sm font-semibold text-brand-indigo">Retail Sales Heads</div>
            <div className="text-[11px] text-muted-foreground">Regional leadership and their mapped RMs</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold">Sales Head</th>
                  <th className="px-5 py-3 font-semibold">Region</th>
                  <th className="px-5 py-3 font-semibold">Mapped RMs</th>
                  <th className="px-5 py-3 font-semibold text-right">Clients</th>
                  <th className="px-5 py-3 font-semibold text-right">Sessions</th>
                </tr>
              </thead>
              <tbody>
                {shRows.map(({ sh, mappedRms, clients, sessions }) => (
                  <tr key={sh.user_id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-brand-indigo">{sh.name}</div>
                      <div className="text-[11px] text-muted-foreground">{sh.email}</div>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{sh.title.replace('Retail Sales Head — ', '')}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {mappedRms.map((r) => (
                          <span key={r.user_id} className="text-[10px] font-semibold uppercase tracking-wider bg-brand-bg border border-brand-border text-brand-indigo px-2 py-0.5 rounded-full">{r.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-brand-indigo">{clients}</td>
                    <td className="px-5 py-3 text-right font-semibold text-brand-indigo">{sessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-brand-indigo">Relationship Manager Performance</div>
              <div className="text-[11px] text-muted-foreground">Client book &amp; MRI throughput by RM</div>
            </div>
            <Link to="/admin/team" className="text-xs text-brand-indigo font-semibold hover:underline flex items-center gap-1">Team view <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold">RM</th>
                  <th className="px-5 py-3 font-semibold">Reports To</th>
                  <th className="px-5 py-3 font-semibold">EUIN</th>
                  <th className="px-5 py-3 font-semibold text-right">Clients</th>
                  <th className="px-5 py-3 font-semibold text-right">Sessions</th>
                  <th className="px-5 py-3 font-semibold text-right">Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {rmRows.map(({ rm, sh, clients, sessions, avg }) => {
                  const c = scoreColor(avg);
                  return (
                    <tr key={rm.user_id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-brand-indigo">{rm.name}</div>
                        <div className="text-[11px] text-muted-foreground">{rm.email}</div>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{sh?.name || '—'}</td>
                      <td className="px-5 py-3 text-muted-foreground">{rm.euin}</td>
                      <td className="px-5 py-3 text-right font-semibold">{clients}</td>
                      <td className="px-5 py-3 text-right font-semibold">{sessions}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={`h-2 w-2 rounded-full ${c.bg}`} />
                          <span className={`font-bold ${c.text}`}>{avg || '—'}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <RMLeaderboard />

      <AlertsFeed compact />

      <DigestAnalytics />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-brand-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-brand-amber" />
              <div className="text-sm font-semibold text-brand-indigo">Follow-ups Due (7 days)</div>
            </div>
            {followupsWeek.length === 0 && <div className="text-sm text-muted-foreground">All clear · no follow-ups this week.</div>}
            <div className="space-y-2">
              {followupsWeek.slice(0, 6).map((s) => {
                const client = visibleClients.find((c) => c.id === s.client_id);
                const rm = allUsers.find((u) => u.user_id === s.rm_id);
                return (
                  <Link key={s.id} to={`/clients/${s.client_id}/mri/${s.id}/report`} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-brand-bg">
                    <div>
                      <div className="text-sm font-medium text-brand-indigo">{client?.name}</div>
                      <div className="text-[11px] text-muted-foreground">RM: {rm?.name} · {fmtDate(s.follow_up_date)}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-brand-green" />
              <div className="text-sm font-semibold text-brand-indigo">Compliance Snapshot</div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">KYC verified clients</span>
                <span className="font-semibold text-brand-indigo">{visibleClients.filter((c) => c.kyc_status === 'verified').length} / {visibleClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Sessions with signed report</span>
                <span className="font-semibold text-brand-indigo">{visibleSessions.filter((s) => s.signed_report).length} / {visibleSessions.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Sessions with conversation log</span>
                <span className="font-semibold text-brand-indigo">{visibleSessions.filter((s) => (s.conversation_log?.length || 0) > 0).length} / {visibleSessions.length}</span>
              </div>
              <div className="pt-2 border-t border-brand-border text-[11px] text-muted-foreground leading-relaxed">
                All session records retained for a minimum of 5 years as required under AMFI Code of Conduct.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
